import type { Scope } from './clubSession';

// ═══════════════════════════════════════════════════════════════════
// THE PORTAL'S CONTEXT SWITCHER — WHICH CONTEXTS A SIGNED-IN PERSON MAY
// SWITCH BETWEEN (owner, 2026-10-20, settled 2026-10-21).
//
// «Book as a player, book as the club, book as the centre.» It switches WHO
// YOU ACT AS — yourself, or one of your organizations — and never a role:
//
//   player        «Spiller» — the account has a player profile (`/me/player`).
//   organization  ONE per scope in `/auth/scopes`, however many roles the
//                 person holds there. Within an organization the roles COMBINE
//                 — the server's capability union, decided on every request —
//                 so there is nothing to choose between, and no single role is
//                 selectable. Named by the ORGANIZATION (its name, its kind
//                 beneath), never by a role: «Klubbadmin» would be wrong for an
//                 activity leader, and a role key is data that can be renamed.
//
// There is NO platform context. `/sa` is its own entrance, signed in with
// Entra (#1002); a door to it from a customer session would lead to a refusal.
//
// A VIEW CHOICE, NOT AN AUTHORITY CHANGE. Choosing a context changes which
// surface and which organization the screens address. The server re-decides
// on every request; a list edited in the browser changes what is DRAWN and
// nothing about what may be DONE.
//
// Pure, so the listing is testable without a session or a server.
// ═══════════════════════════════════════════════════════════════════

export type RoleContext =
  | { kind: 'player' }
  | { kind: 'organization'; tenantId: string; slug: string; name: string; orgKind: string };

export interface RoleContextInputs {
  /** Where this account may act — `/auth/scopes`, never roster membership. */
  scopes: readonly Scope[];
  /** Offer «Spiller»: the account has a player profile. */
  player: boolean;
}

/**
 * Every context this account holds, in the order the menu shows them: the
 * player first (everybody's own), then the organizations in the server's order.
 */
export function roleContexts({ scopes, player }: RoleContextInputs): RoleContext[] {
  const out: RoleContext[] = [];
  if (player) out.push({ kind: 'player' });
  for (const s of scopes) {
    out.push({
      kind: 'organization',
      tenantId: s.tenantId,
      slug: s.slug,
      name: s.name,
      orgKind: s.kind,
    });
  }
  return out;
}

/** A stable key per context — for React and for «which one is current». */
export function roleContextKey(c: RoleContext): string {
  if (c.kind === 'organization') return organizationContextKey(c.tenantId);
  return c.kind;
}

/** The key of the organization context for one tenant. */
export function organizationContextKey(tenantId: string): string {
  return `org:${tenantId}`;
}

/**
 * Whether a switcher is worth drawing at all.
 *
 * ONE CONTEXT IS NOTHING TO SWITCH BETWEEN. A person who only administers one
 * centre sees no control — not a disabled one, and not a menu that opens onto
 * the place they already are.
 */
export function switcherOffered(contexts: readonly RoleContext[]): boolean {
  return contexts.length > 1;
}

/**
 * The catalog key for an organization's KIND, shown beneath its name — the
 * same «Senter» / «Klubb» the switcher has always used (#752). Null for a kind
 * this build has no word for: a future organization type then shows its name
 * alone until a catalog entry is added, rather than a guess.
 */
export function orgKindLabelKey(
  orgKind: string,
): 'auth.scope.kind.centre' | 'auth.scope.kind.club' | null {
  if (orgKind === 'CENTRE') return 'auth.scope.kind.centre';
  if (orgKind === 'CLUB') return 'auth.scope.kind.club';
  return null;
}
